import { getCredentials, getConfig } from './woo';

/**
 * Servicio de validación de años para customers
 */

/**
 * Obtener validación de año para un customer
 * @param {number} customerId - ID del customer
 * @param {number} targetYear - Año objetivo (opcional, por defecto año siguiente)
 * @returns {Promise<Object>} Datos de validación
 */
export const getCustomerYearValidation = async (customerId, targetYear = null) => {
  try {
    const credentials = getCredentials();
    const apiUrl = getConfig(credentials, "WP_API_URL") || "https://pagos.blaspascal.cl/wp-json";
    
    let endpoint = `${apiUrl}/datofy/v1/customer-year-validation/${customerId}`;
    if (targetYear) {
      endpoint += `?target_year=${targetYear}`;
    }
    
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      throw new Error('Error al obtener validación de año');
    }
    
    const data = await response.json();
    
    return {
      success: true,
      data: data,
      canViewNextYear: data.can_view_next_year,
      canMarkCompleted: data.can_mark_completed,
      pendingPreviousYear: data.pending_previous_year,
      message: data.message,
      alertMessage: data.alert_message
    };
    
  } catch (error) {
    console.error('Error en validación de año:', error);
    return {
      success: false,
      error: error.message,
      canViewNextYear: false,
      canMarkCompleted: false,
      pendingPreviousYear: 0,
      message: 'Error al validar año',
      alertMessage: 'Error al validar permisos de año'
    };
  }
};

/**
 * Validar si un admin puede marcar órdenes como completadas
 * @param {number} customerId - ID del customer
 * @param {number} orderYear - Año de las órdenes a marcar como completadas
 * @returns {Promise<Object>} Resultado de validación
 */
export const validateAdminCompleteAction = async (customerId, orderYear) => {
  try {
    const validation = await getCustomerYearValidation(customerId, orderYear);
    
    if (!validation.success) {
      return {
        canProceed: false,
        requiresConfirmation: false,
        message: validation.error,
        type: 'error'
      };
    }
    
    const currentYear = new Date().getFullYear();
    
    // Si es el año actual o anterior, permitir sin restricciones
    if (orderYear <= currentYear) {
      return {
        canProceed: true,
        requiresConfirmation: false,
        message: 'Acción permitida',
        type: 'success'
      };
    }
    
    // Si es año futuro y tiene pendientes del año anterior
    if (orderYear > currentYear && validation.pendingPreviousYear > 0) {
      return {
        canProceed: true,
        requiresConfirmation: true,
        message: `⚠️ El usuario tiene ${validation.pendingPreviousYear} pagos pendientes de ${orderYear - 1}. ¿Desea continuar marcando órdenes de ${orderYear} como completadas?`,
        type: 'warning',
        pendingCount: validation.pendingPreviousYear,
        previousYear: orderYear - 1,
        targetYear: orderYear
      };
    }
    
    // Si es año futuro sin pendientes
    return {
      canProceed: true,
      requiresConfirmation: false,
      message: `Usuario al día con pagos de ${orderYear - 1}. Acción permitida.`,
      type: 'success'
    };
    
  } catch (error) {
    console.error('Error en validación admin:', error);
    return {
      canProceed: false,
      requiresConfirmation: false,
      message: 'Error al validar permisos',
      type: 'error'
    };
  }
};

/**
 * Mostrar alerta informativa cuando admin visualiza año futuro con pendientes
 * @param {number} customerId - ID del customer
 * @param {number} viewingYear - Año que está visualizando
 * @returns {Promise<Object>} Información para mostrar
 */
export const getAdminYearViewInfo = async (customerId, viewingYear) => {
  try {
    const currentYear = new Date().getFullYear();
    
    // Solo mostrar info para años futuros
    if (viewingYear <= currentYear) {
      return { shouldShow: false };
    }
    
    const validation = await getCustomerYearValidation(customerId, viewingYear);
    
    if (!validation.success) {
      return { shouldShow: false };
    }
    
    if (validation.pendingPreviousYear > 0) {
      return {
        shouldShow: true,
        message: `ℹ️ Visualizando órdenes de ${viewingYear}. El usuario tiene ${validation.pendingPreviousYear} documentos pendientes de ${viewingYear - 1}.`,
        type: 'info',
        pendingCount: validation.pendingPreviousYear,
        previousYear: viewingYear - 1,
        targetYear: viewingYear
      };
    }
    
    return { shouldShow: false };
    
  } catch (error) {
    console.error('Error obteniendo info de año:', error);
    return { shouldShow: false };
  }
};